import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDashboardData, Course } from "@/context/DashboardDataContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CourseCard, CourseCardSkeleton } from "@/components/dashboard/CourseCard";
const INITIAL_VISIBLE = 6;
export const ExploreCourses: React.FC = () => {
  const {
    categories,
    courses,
    isLoading,
    isError,
    errorMessage
  } = useDashboardData();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("for-you");
  const [showAll, setShowAll] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const updateScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    updateScrollButtons();
    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [updateScrollButtons, categories]);

  const handleScrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: 200,
      behavior: "smooth"
    });
  };
  const handleScrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: -200,
      behavior: "smooth"
    });
  };
  const filteredCourses = useMemo(() => {
    if (selectedCategoryId === "for-you") {
      return courses;
    }
    return courses.filter((c) => c.categoryId && c.categoryId === selectedCategoryId);
  }, [courses, selectedCategoryId]);
  const visibleCourses: Course[] = useMemo(() => {
    if (showAll) return filteredCourses;
    return filteredCourses.slice(0, INITIAL_VISIBLE);
  }, [filteredCourses, showAll]);
  const hasMore = filteredCourses.length > INITIAL_VISIBLE;
  return <section className="w-full mb-10" data-explore-courses-section="true">
      {/* Header */}
      <div className="mb-6">
        <div className="w-full flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="text-base lg:text-lg font-semibold text-foreground">Explore Courses</span>
            
          </div>
        </div>

        {/* Category tabs with horizontal scroll */}
        <div className="w-full relative group/close mb-4">
          {canScrollLeft && <button type="button" onClick={handleScrollLeft} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none hover:text-accent-foreground h-10 w-10 absolute left-1 top-1/2 z-[7] -mt-1 -translate-y-1/2 rounded-full border border-border bg-background/70 shadow-sm backdrop-blur transition-all hover:bg-background/80 opacity-0 group-hover/close:opacity-100" aria-label="Scroll left">
              <ChevronLeft className="h-5 w-5" />
            </button>}

          {canScrollRight && <button type="button" onClick={handleScrollRight} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none hover:text-accent-foreground h-10 w-10 absolute right-1 top-1/2 z-[7] -mt-1 -translate-y-1/2 rounded-full border border-border bg-background/70 shadow-sm backdrop-blur transition-all hover:bg-background/80 opacity-0 group-hover/close:opacity-100" aria-label="Scroll right">
              <ChevronRight className="h-5 w-5" />
            </button>}

          {/* Scroll container */}
          <div className="w-full overflow-x-auto scrollbar-hide" ref={scrollContainerRef}>
            <div className="flex gap-2 pb-2 px-0.5 min-w-max">
              {/* For You tab */}
              <button type="button" onClick={() => setSelectedCategoryId("for-you")} className={["inline-flex items-center justify-center text-sm ring-offset-background focus-visible:outline-none h-9 rounded-lg px-3 group mt-0.5 flex-shrink-0 whitespace-nowrap font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200", selectedCategoryId === "for-you" ? "border border-primary bg-primary text-primary-foreground ring-1 ring-primary/20 hover:bg-primary/90" : "bg-card border border-primary/10 text-primary/90 hover:border-primary/20 hover:bg-card hover:text-primary dark:border-primary/5 dark:hover:border-primary/10"].join(" ")}>
                For You
              </button>

              {/* Dynamic category tabs */}
              {categories.map((category) => {
              const isActive = selectedCategoryId === category.id;
              return <button key={category.id} type="button" onClick={() => setSelectedCategoryId(category.id)} className={["inline-flex items-center justify-center text-sm ring-offset-background focus-visible:outline-none h-9 rounded-lg px-3 group mt-0.5 flex-shrink-0 whitespace-nowrap font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200", isActive ? "border border-primary bg-primary text-primary-foreground ring-1 ring-primary/20 hover:bg-primary/90" : "bg-card border border-primary/10 text-primary/90 hover:border-primary/20 hover:bg-card hover:text-primary dark:border-primary/5 dark:hover:border-primary/10"].join(" ")}>
                    <span className="truncate">{category.name}</span>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? <ExploreCoursesSkeleton /> : isError ? <div className="text-sm text-muted-foreground bg-muted/5 border border-muted/20 rounded-lg px-3 py-2">
          {errorMessage ?? "Something went wrong while loading courses."}
        </div> : filteredCourses.length === 0 ? <p className="text-sm text-muted-foreground">
          Browse public courses to start learning.
        </p> : <>
          {/* Course grid */}
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6 py-1">
            {visibleCourses.map((course) => <CourseCard key={course.id} href={course.url ?? `/course/${course.id}`} title={course.title} author={course.author} coverImageUrl={course.coverImageUrl} coverUrl={course.coverUrl} iconUrl={course.iconUrl} iconEmoji={course.iconEmoji} accentColor={course.accentColor} />)}
          </div>

          {/* Show More / Show Less */}
          {hasMore && <div className="flex justify-center mt-6">
              <button type="button" onClick={() => setShowAll((prev) => !prev)} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none bg-transparent h-9 rounded-lg px-4 gap-2 text-primary hover:text-primary/80">
                <span>{showAll ? "Show Less" : "Show More"}</span>
                <ChevronRight className={`h-4 w-4 transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />
              </button>
            </div>}
        </>}
    </section>;
};

// ------------ Skeleton ------------

const ExploreCoursesSkeleton: React.FC = () => {
  const skeletonItems = Array.from({
    length: INITIAL_VISIBLE
  });
  return <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6 py-1">
      {skeletonItems.map((_, idx) => <CourseCardSkeleton key={idx} />)}
    </div>;
};